import { useDispatch, useSelector } from "react-redux";
import { selectToken } from "../app/slices/AuthenticationSlice";
import {
  selectAutoConnectToUnity,
  setIsLoading,
} from "../app/slices/GlobalSlice";
import {
  rebaseCurrentDocument,
  resetHistory,
} from "../app/slices/HistorySlice";
import { setCurrentTrainingId } from "../app/slices/ProjectsSlice";
import {
  clearLocalization,
  hydrateLocalization,
  type LanguageData,
  type LocalizedStringRecord,
} from "../app/slices/TrainingLocalizationSlice";
import {
  TrainingType,
  clearTraining,
  loadReferenceLanguage,
  loadTrainingMetadata,
} from "../app/slices/TrainingSlice";
import { selectCurrentOrganization } from "../app/slices/UserSlice";
import { TrainingContentDTO } from "../dto/TrainingContentDTO";
import { Training } from "../models/Training";
import { TrainingService } from "../services/TrainingService";
import { WebSocketService } from "../services/WebSocketService";
import { useCreatorWebSocketConnector } from "../features/send_to_unity/CreatorWebSocketConnector";
import useLoadTraining360Content from "./LoadTraining360ContentHook";
import useLoadTraining3DContent from "./LoadTraining3DContentHook";

export const useLoadTraining = () => {
  const dispatch = useDispatch();
  const orgId = useSelector(selectCurrentOrganization)?.id;
  const token = useSelector(selectToken);
  const autoConnectToUnity = useSelector(selectAutoConnectToUnity);
  const { loadTraining360Content } = useLoadTraining360Content();
  const { loadTraining3DContent } = useLoadTraining3DContent();
  const { connect } = useCreatorWebSocketConnector();

  const loadTraining = async (trainingId: string) => {
    if (!orgId || !token) return;

    dispatch(setIsLoading(true));
    dispatch(resetHistory());
    dispatch(clearLocalization());
    dispatch(clearTraining());
    let trainingDTO: TrainingContentDTO | undefined;

    try {
      trainingDTO = await TrainingService.GetTrainingContent(
        orgId,
        token,
        trainingId
      );
      if (!trainingDTO) throw new Error("Training content was not found.");
      var trainingContent: Training | undefined = await loadTrainingContent(
        trainingDTO
      );
      if (!trainingContent) {
        throw new Error("Training document could not be loaded.");
      }
      await loadTrainingLocalizations(
        trainingId,
        trainingContent?.refLanguage ?? ""
      );

      if (
        trainingDTO?.type === TrainingType.Training3D &&
        autoConnectToUnity &&
        !WebSocketService.isConnected()
      ) {
        connect();
      }

      dispatch(rebaseCurrentDocument());

      document.title = trainingDTO
        ? trainingDTO.name + " - Play It Creator"
        : "Play It Creator";

      // needed to check if load was succesful or not
      return trainingDTO !== undefined;
    } catch {
      dispatch(clearLocalization());
      dispatch(clearTraining());
      dispatch(resetHistory());
      document.title = "Play It Creator";
      return false;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const loadTrainingContent = async (
    trainingContent: TrainingContentDTO | undefined
  ): Promise<Training | undefined> => {
    if (!trainingContent) return undefined;

    // required maintenance
    dispatch(setCurrentTrainingId(trainingContent.id));
    dispatch(
      loadTrainingMetadata({
        name: trainingContent.name,
        description: trainingContent.description,
        type: trainingContent.type,
      })
    );

    // loading the actual data
    var training = await TrainingService.GetTrainingJson(
      trainingContent.downloadUri
    );
    if (!training) return undefined;

    if (trainingContent.type === TrainingType.Training360) {
      await loadTraining360Content(training);
    } else {
      await loadTraining3DContent(training, token!, orgId!);
    }

    return training;
  };

  const loadTrainingLocalizations = async (
    trainingId: string,
    referenceLanguage: string
  ) => {
    var languages = await TrainingService.GetLanguages(token!);
    const availableLanguages: LanguageData[] = languages.map((language) => ({
      shortcode: language.id,
      full: language.name,
    }));

    // load training translations
    var trainingTranslations =
      (await TrainingService.GetTrainingTranslations(orgId!, token!, trainingId)) ??
      [];

    if (trainingTranslations.length > 0) {
      const localizedEntries: LocalizedStringRecord = {};
      const localizedLanguages: LanguageData[] = trainingTranslations.map(
        (translation) => ({
          ...(availableLanguages.find(
            (language) => language.shortcode === translation.lang
          ) ?? { shortcode: translation.lang, full: translation.lang }),
          includeInPublish: translation.includeInPublish ?? true,
        })
      );
      trainingTranslations.forEach((translation) => {
        translation.entries.forEach((entry) => {
          localizedEntries[entry.key] ??= {};
          localizedEntries[entry.key][translation.lang] = {
            value: entry.value,
            isAutoTranslated: entry.isAutoTranslated,
          };
        });
      });

      var initialLanguage = trainingTranslations[0].lang;
      if (
        referenceLanguage !== "" &&
        trainingTranslations.find((t) => t.lang === referenceLanguage)
      ) {
        initialLanguage = referenceLanguage;
      }
      const initialLanguageData = localizedLanguages.find(
        (language) => language.shortcode === initialLanguage
      )!;
      const orderedLanguages = [
        initialLanguageData,
        ...localizedLanguages.filter(
          (language) => language.shortcode !== initialLanguage
        ),
      ];
      dispatch(
        hydrateLocalization({
          localizedEntries,
          localizedLanguages: orderedLanguages,
          availableLanguages,
          currentLanguage: initialLanguageData,
          initialized: true,
        })
      );
      dispatch(loadReferenceLanguage(initialLanguage));
    } else {
      const referencedLanguage =
        availableLanguages.find(
          (language) => language.shortcode === referenceLanguage
        );
      const currentLanguage =
        referencedLanguage ??
        availableLanguages[0] ?? { shortcode: "", full: "" };
      dispatch(
        hydrateLocalization({
          localizedEntries: {},
          localizedLanguages: referencedLanguage
            ? [referencedLanguage]
            : [],
          availableLanguages,
          currentLanguage,
          initialized: referencedLanguage !== undefined,
        })
      );
      dispatch(loadReferenceLanguage(referenceLanguage));
    }
  };

  return [loadTraining] as const;
};
